import React, { useEffect, useState } from "react";

import { Label, LoadingIcon as Loading, Skeleton } from "@packages/ds";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Error from "Components/Error";
import IdeField from "Components/fields/IdeField";
import { APP_NAME } from "Constants/documentationUrls";
import { THIRD_PARTIES } from "Constants/integrationThirdParties";
import Button, { Link } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Input from "ds/Input";
import Select from "ds/Select";
import TextareaDs from "ds/Textarea";
import useInput from "Hooks/useInput";
import { capitalize } from "Libs/utils";
import { getGitRepositoriesIntegration, initGit } from "Reducers/integration";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import AuthenticationNameBox from "../AuthenticationNameBox";
import IntegrationIcon from "../IntegrationIcon";
import IntegrationScopesText from "../IntegrationScopesText";

import * as S from "./IntegrationForm.styles";

import type { Integration } from "@packages/client";
import type { IntegrationField } from "Reducers/integration/types";

const getFields = (list: Readonly<IntegrationField[]>, parentId?: string) => {
  return list
    .reduce(
      (acc, cu) => {
        const obj = Object.assign({}, cu);
        if (parentId) {
          obj.id = `${parentId}/${cu.id}`;
        }

        if (cu.type === "object") {
          acc.push(getFields(cu.fields!, obj.id));
        } else {
          acc.push(obj);
        }
        return acc;
      },
      [] as (IntegrationField | IntegrationField[])[]
    )
    .flat();
};

const getValue = (path: string, data?: any): any => {
  if (!data) return;
  const entries = path.split("/");
  const key = entries.shift();

  const value = key ? data[key] : undefined;
  if (entries.length && value) {
    return getValue(entries.join("/"), value);
  }
  return value;
};

type IntegrationFormProps = {
  integration?: Integration;
  onSubmit: (data: Record<string, unknown>, type?: string) => void;
  onCancel?: () => void;
  onRemove?: () => void;
  integrationAction?: "create";
};

const IntegrationForm = ({
  onCancel,
  onRemove,
  onSubmit,
  integration,
  integrationAction
}: IntegrationFormProps) => {
  const params = useParams<{ type: string }>();

  const type = integration ? integration.type : params.type;
  const isEditingGithubIntegration = type === "github" && integration?.id;
  const [isFirstStepConfiguration, setIsFirstStepConfiguration] = useState(
    isEditingGithubIntegration ? false : true
  );
  const [repositoryName, setRepositoryName] = useState<string | undefined>(
    isEditingGithubIntegration ? integration?.repository : undefined
  );

  const canEdit = integration ? integration?.hasLink("#edit") : true;
  const canDelete = integration ? integration?.hasLink("#delete") : true;

  const dispatch = useAppDispatch();
  const intl = useIntl();

  const errorMessage = useAppSelector(state => state.integration.errorMessage);

  const status = useAppSelector(state => state.integration.git?.status);

  const loading = useAppSelector(state => state.integration.loading);

  const gitCheckedBoxes = [
    "fetch_branches",
    "prune_branches",
    "build_pull_requests",
    "resync_pull_requests"
  ];

  useEffect(() => {
    setTimeout(() => {
      document.getElementById("token")?.focus();
    }, 0);
  }, [dispatch, integration?.id]);

  useEffect(() => {
    if (status === "fulfilled") {
      setIsFirstStepConfiguration(false);
    }
    return () => {
      dispatch(initGit());
    };
  }, [dispatch, status]);

  const config = THIRD_PARTIES.find(
    elt => elt.type === type?.replace("health_", "health.")
  );

  const fields = config?.fields ? getFields(config.fields) : [];

  const getInitialValues = () => {
    if (!config) return {};

    const initialValues = fields.reduce<Record<string, string>>((acc, cu) => {
      const fieldValue = getValue(cu.id, integration) || "";

      if (cu.type === "array") {
        acc[cu.id] = Array.isArray(fieldValue)
          ? fieldValue.join(cu.separator ? cu.separator : " ")
          : fieldValue;
      } else {
        acc[cu.id] = fieldValue;
      }
      return acc;
    }, {});

    // Only options that are available from the integration state should be kept
    // This will correctly display the checkbox as checked or not.
    const gitCheckedBoxesReviewed = gitCheckedBoxes.filter(
      option => !!integration?.[option]
    );

    const initialOptions = config.options?.reduce<Record<string, boolean>>(
      (acc, cu) => {
        if (
          // On integration creation all checkboxes should be checked
          integrationAction === "create" ||
          gitCheckedBoxesReviewed.includes(cu)
        ) {
          acc[cu] = true;
        } else {
          acc[cu] = integration?.[cu] || false;
        }
        return acc;
      },
      {}
    );

    return Object.assign({}, initialValues, initialOptions);
  };

  const { inputs, handleInputChange, initialValues } = useInput(
    getInitialValues() as Record<string, string | undefined>
  );

  const gitData = useAppSelector(state =>
    inputs.token ? state.integration.git?.data?.[inputs.token] : undefined
  );

  if (!config) return <p>Error</p>;

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (config.type === "github") {
      inputs.repository = repositoryName;
    } else if (config.type === "gitlab") {
      inputs.project = repositoryName;
    }
    if (canSubmit()) onSubmit(formatData(), type);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();

    onCancel?.();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    onRemove?.();
  };

  const onChangeRepositoryName = (repositoryName: {
    value?: string;
    label?: string;
  }) => {
    setRepositoryName(repositoryName.value ?? undefined);
  };

  const canSubmit = () => {
    if (initialValues === inputs) return false;
    for (const field of fields) {
      // sensitive fields are not return by the api
      if (integration && field.sensitive === true) continue;
      if (!!field.required && !inputs[field.id]) return false;
    }
    return true;
  };

  const formatData = () => {
    return Object.entries(inputs).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        const field = fields.find(field => field.id === key);
        let formattedValue: string | string[] | undefined = value;

        // Add default value if field is empty
        if (formattedValue === "" && field?.default)
          formattedValue = field.default;

        // Transform string to  array
        if (field?.type === "array" && typeof formattedValue === "string") {
          formattedValue = formattedValue
            .split(field.separator ? field.separator : " ")
            .map(elt => elt.trim())
            .filter(elt => elt); // remove empty element
        }

        key.split("/").reduce((acc1, cu, i, arr) => {
          if (formattedValue !== "") {
            return (acc1[cu] ||
              (acc1[cu] = arr[i + 1] ? {} : formattedValue)) as any;
          }
        }, acc);

        return acc;
      },
      {}
    );
  };

  const getIntlKey = (key: string) => {
    if (intl.messages[`integration.configure.${type}.${key}`] !== undefined)
      return `integration.configure.${type}.${key}`;
    return `integration.configure.${key}`;
  };

  const hasKey = (key: string) => {
    return intl.messages[`integration.configure.${key}`] !== undefined;
  };

  const isTextarea = (field: IntegrationField): boolean =>
    field.type === "array" || field.kind === "textarea";

  const getFieldComp = (field: IntegrationField) => {
    if (field.kind === "ide") return IdeField;
    return Input;
  };

  const handleGetRepositories = (e: React.MouseEvent) => {
    e?.preventDefault();
    if (isEditingGithubIntegration) {
      onSubmit({ token: inputs.token }, type);
      return;
    }
    if (inputs.base_url) {
      setIsFirstStepConfiguration(false);
      return;
    }
    dispatch(
      getGitRepositoriesIntegration({
        baseUrl: inputs.base_url,
        token: inputs.token!,
        type: config.type
      })
    );
  };

  const handleChangeToken = (e: React.MouseEvent) => {
    e?.preventDefault();

    setIsFirstStepConfiguration(true);
    setRepositoryName(undefined);
  };

  const isGitIntegraton = ["github", "gitlab"].includes(config.type);
  const repoSelection = gitData?.repositories?.find(
    repo => repo.value === repositoryName
  );

  return (
    <S.Form>
      <S.Header>
        <IntegrationIcon type={config.type} />
        <S.Title tag="h2">
          {intl.formatMessage({
            id: `integration.type.${config.type}`,
            defaultMessage: type
          })}
        </S.Title>
        {config?.link && (
          <p>
            {intl.formatMessage(
              {
                id: getIntlKey("intro"),
                defaultMessage: "Intro"
              },
              {
                b: (...chunks) => <b>{chunks}</b>,
                br: <br />,
                a: (...chunks) => (
                  <Link analyticId={config.link} href={config.link!}>
                    {chunks}
                  </Link>
                ),
                appName: APP_NAME
              }
            )}
          </p>
        )}
      </S.Header>
      {errorMessage && <Error>{errorMessage}</Error>}
      {fields?.map(field => {
        const FieldComp = getFieldComp(field);
        return (
          <S.Field
            key={`field-${field.id}`}
            className={field.kind === "ide" ? "large" : ""}
          >
            {!isFirstStepConfiguration ? (
              <>
                {loading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" width="520px" />
                  </Skeleton>
                ) : (
                  !!gitData && (
                    <AuthenticationNameBox
                      name={gitData?.user.name}
                      avatarUrl={gitData?.user.avatarUrl}
                      onClickChangeToken={handleChangeToken}
                    />
                  )
                )}
                {gitData?.repositories ? (
                  <S.SelectWrapper>
                    <Label htmlFor="integration-form-repo" required>
                      {intl.formatMessage({
                        id: "integration.configure.field.repository.git.label",
                        defaultMessage: "Repositories"
                      })}
                    </Label>
                    <Select
                      id="integration-form-repo"
                      classNamePrefix="countries-creation-form"
                      options={gitData?.repositories}
                      value={repoSelection}
                      onChange={option => {
                        onChangeRepositoryName(
                          option as {
                            value: string;
                            label: string;
                          }
                        );
                      }}
                      placeholder={intl.formatMessage({
                        id: "integration.configure.field.repository.git.placeholder",
                        defaultMessage: "Choose repository"
                      })}
                    />
                  </S.SelectWrapper>
                ) : isEditingGithubIntegration ? (
                  <>
                    <S.DropdownWrapper>
                      <S.SelectWrapper>
                        <Label htmlFor="integration-form-repo" required>
                          {intl.formatMessage({
                            id: "integration.configure.field.repository.git.label",
                            defaultMessage: "Repositories"
                          })}
                        </Label>
                        <Select
                          id="integration-form-repo"
                          classNamePrefix="countries-creation-form"
                          value={{
                            label: integration?.repository,
                            value: integration?.repository
                          }}
                          isDisabled
                          required
                        />
                      </S.SelectWrapper>
                      {integration?.token_type !== "github_app" && (
                        <Button
                          variant="link"
                          analyticId="integration.configure.field.info.change_token"
                          onClick={handleChangeToken}
                        >
                          {intl.formatMessage({
                            id: "integration.configure.field.info.change_token"
                          })}
                        </Button>
                      )}
                    </S.DropdownWrapper>
                    <S.Disclaimer>
                      {intl.formatMessage({
                        id: "integration.configure.field.repository.git.disclaimer",
                        defaultMessage:
                          "Repositories can only be changed by removing the existing integration and creating a new one."
                      })}
                    </S.Disclaimer>
                  </>
                ) : (
                  <Input
                    analyticId={getIntlKey(`field.repository_name.label`)}
                    id="repository_name"
                    name="repository_name"
                    label={intl.formatMessage({
                      id: getIntlKey(`field.repository_name.label`),
                      defaultMessage: "repository name"
                    })}
                    placeholder={intl.formatMessage({
                      id: getIntlKey(`field.repository_name.placeholder`),
                      defaultMessage: "repository name"
                    })}
                    required={true}
                    value={repositoryName}
                    onChange={e => {
                      onChangeRepositoryName({
                        value: e.target.value,
                        label: e.target.value
                      });
                    }}
                    width="100%"
                  />
                )}
              </>
            ) : isTextarea(field) && field?.type !== "ide" ? (
              <TextareaDs
                id={field.id}
                name={field.id}
                label={
                  <S.DialogWrapper>
                    <S.Label required={!!field.required}>
                      {intl.formatMessage({
                        id: getIntlKey(`field.${field.id}.label`),
                        defaultMessage: field.id
                      })}
                    </S.Label>
                    {hasKey(`field.${field.id}.info.title`) && (
                      <Dialog
                        title={intl.formatMessage({
                          id: getIntlKey(`field.${field.id}.info.title`)
                        })}
                        analyticId={`field.${field.id}.info.text`}
                        description={intl.formatMessage({
                          id: getIntlKey(`field.${field.id}.info.text`)
                        })}
                        align="center"
                        link={
                          <Link
                            href={intl.formatMessage({
                              id: "links.documentation.integrations.activity_scripts"
                            })}
                            analyticId="learnmore"
                          >
                            {intl.formatMessage({
                              id: "learnmore"
                            })}
                          </Link>
                        }
                      />
                    )}
                  </S.DialogWrapper>
                }
                placeholder={intl.formatMessage({
                  id: getIntlKey(`field.${field.id}.placeholder`),
                  defaultMessage: field.id
                })}
                value={inputs[field.id]}
                onChange={handleInputChange}
                height={240}
                isValid={true}
              />
            ) : (
              <FieldComp
                id={field.id}
                name={field.id}
                analyticId={getIntlKey(`field.${field.id}.label`)}
                label={intl.formatMessage({
                  id: getIntlKey(`field.${field.id}.label`),
                  defaultMessage: field.id
                })}
                placeholder={intl.formatMessage({
                  id: getIntlKey(`field.${field.id}.placeholder`),
                  defaultMessage: field.id
                })}
                required={!!field.required}
                value={inputs[field.id]}
                onChange={handleInputChange}
                options={{ lineNumbers: false }}
                expandable={true}
                height="280px"
                width="100%"
              />
            )}
          </S.Field>
        );
      })}

      {(!isGitIntegraton || !isFirstStepConfiguration) &&
      config.options?.length ? (
        <S.Options>
          {config.options?.map(option => (
            <Checkbox
              key={`options.${option}`}
              onCheckedChanged={checked => {
                handleInputChange({
                  target: {
                    type: "checkbox",
                    checked,
                    name: option
                  }
                } as React.ChangeEvent<HTMLInputElement>);
              }}
              checked={!!inputs[option]}
              label={intl.formatMessage<React.ReactNode>(
                {
                  id: getIntlKey(`option.${option}`),
                  defaultMessage: option
                },
                {
                  i: chunks => <i>{chunks}</i>
                }
              )}
              forId={option}
              id={getIntlKey(`option.${option}`)}
            />
          ))}
        </S.Options>
      ) : (
        isGitIntegraton && (
          <>
            {config?.link && type && (
              <IntegrationScopesText type={type} link={config.link} />
            )}
            <Input
              id="base_url"
              name="base_url"
              analyticId={getIntlKey(`field.base_url.label`)}
              label={intl.formatMessage({
                id: getIntlKey(`field.base_url.label`),
                defaultMessage: "base_url"
              })}
              placeholder={intl.formatMessage({
                id: getIntlKey(`field.base_url.placeholder`),
                defaultMessage: "base_url"
              })}
              required={false}
              value={inputs.base_url}
              onChange={handleInputChange}
              width="100%"
            />
          </>
        )
      )}
      <S.Footer>
        {!isGitIntegraton || !isFirstStepConfiguration ? (
          <>
            {isGitIntegraton ? (
              <>
                {!isEditingGithubIntegration && (
                  <Button
                    className="secondary"
                    variant="secondary"
                    onClick={handleChangeToken}
                    analyticId="back"
                  >
                    {intl.formatMessage({ id: "back" })}
                  </Button>
                )}
                <Button
                  id="save"
                  onClick={handleSubmit}
                  analyticId={integration ? "save" : "integration.add"}
                  aria-label={intl.formatMessage({
                    id: integration ? "save" : "integration.add"
                  })}
                  type="submit"
                  disabled={!repositoryName || loading}
                >
                  {capitalize(
                    intl.formatMessage({
                      id: integration ? "save" : "integration.add"
                    })
                  )}
                </Button>
              </>
            ) : (
              <Button
                id="save"
                onClick={handleSubmit}
                aria-label={intl.formatMessage({
                  id: integration ? "save" : "integration.add"
                })}
                type="submit"
                analyticId={integration ? "save" : "integration.add"}
                disabled={!canSubmit() || loading}
              >
                {capitalize(
                  intl.formatMessage({
                    id: integration ? "save" : "integration.add"
                  })
                )}
              </Button>
            )}
          </>
        ) : loading && !errorMessage ? (
          <Loading />
        ) : (
          canEdit && (
            <Button
              id="continue"
              analyticId="continue"
              onClick={handleGetRepositories}
              aria-label={intl.formatMessage({ id: "continue" })}
              disabled={!canSubmit()}
            >
              {capitalize(intl.formatMessage({ id: "continue" }))}
            </Button>
          )
        )}

        {onCancel && (
          <Button
            id="cancel"
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
            analyticId="cancel"
            aria-label={intl.formatMessage({ id: "cancel" })}
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
        )}
        {onRemove && canDelete && (
          <Button
            id="remove"
            variant="outline"
            analyticId="remove"
            onClick={handleRemove}
            disabled={loading}
            aria-label={intl.formatMessage({ id: "remove" })}
          >
            {capitalize(intl.formatMessage({ id: "remove" }))}
          </Button>
        )}
      </S.Footer>
    </S.Form>
  );
};

export default IntegrationForm;
